import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../../../redux';
import { loadUser, updateUser, loadProjects } from '../../../redux';
import { Project, User } from '../../../models';
import { Projects } from '../../../services';
import UserForm from '../UserForm/UserForm';

async function syncUserProjectMemberships(
    userId: string,
    selectedProjectIds: string[],
    projects: Project[]
): Promise<void> {
    const selected = new Set(selectedProjectIds || []);
    const currentIds = projects
        .filter((project) => project.hasMember(new User({ _id: userId })))
        .map((project) => project._id);

    const toAdd = Array.from(selected).filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !selected.has(id));

    await Promise.all([
        ...toAdd.map((projectId) => Projects.addMembers(projectId, [userId])),
        ...toRemove.map((projectId) => Projects.removeMembers(projectId, [userId])),
    ]);
}

function UserEdit() {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();
    let { id }     = useParams();

    const { user: authUser } = useAppSelector((state: RootState) => state.auth);
    const { user, updating } = useAppSelector((state: RootState) => state.users);
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const canEditProjects = !!authUser?.can('project:members:edit');

    useEffect(() => {
        if (id)
            dispatch(loadUser(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (canEditProjects)
            dispatch(loadProjects());
    }, [dispatch, canEditProjects]);

    const memberProjectIds = useMemo(() => {
        if (!user?._id || !projects?.length)
            return [];
        return projects
            .filter((project: Project) => project.hasMember(user))
            .map((project: Project) => project._id);
    }, [user, projects]);

    const formInitialValues = useMemo(() => ({
        ...user,
        projects: memberProjectIds,
    }), [user, memberProjectIds]);

    const submit = function (values: AnyObj) : void {
        const { projects: selectedProjectIds = [], ...userValues } = values;
        dispatch(updateUser(userValues, false, false, (err: Error | null) => {
            if (err || !canEditProjects || !userValues._id)
                return;

            syncUserProjectMemberships(userValues._id, selectedProjectIds, projects || [])
                .then(() => dispatch(loadProjects()))
                .catch((syncError: Error) => {
                    console.error(syncError);
                });
        }));
    }

    return (
        <div className="UserEdit">
            <header className="bg-white dark:bg-gray-900 border-b dark:border-gray-700 px-4 py-0 sm:px-6 lg:px-8">
                <div className="my-2 mx-auto lg:flex lg:items-center lg:justify-between">
                    <div>
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                            { t('users.edit_user', {name: user?.fullname()}) }
                        </h2>
                    </div>
                </div>
            </header>
            <main className="mx-6 my-6 shadow">
                {user && (user._id === id) && (
                    <UserForm isCreation={false} isLoading={updating === 'pending'} initialValues={formInitialValues} onSubmit={submit} />
                )}
            </main>
        </div>
    );
}

export default UserEdit;
